/**
 * Banner showing description generation status for images
 * 
 * Displays when any images have pending/delayed/failed descriptions
 */

import type { ListingImage } from '../types';

interface DescriptionStatusBannerProps {
  images: ListingImage[];
}

interface StatusCounts {
  generating: number;
  delayed: number;
  failed: number;
}

export function DescriptionStatusBanner({ images }: DescriptionStatusBannerProps) {
  // Count images by status
  const counts: StatusCounts = images.reduce(
    (acc, img) => {
      if (img.description_status === 'generating') acc.generating++;
      else if (img.description_status === 'delayed') acc.delayed++;
      else if (img.description_status === 'failed') acc.failed++;
      return acc;
    },
    { generating: 0, delayed: 0, failed: 0 }
  );

  const totalPending = counts.generating + counts.delayed + counts.failed;

  // Don't show banner if no issues
  if (totalPending === 0) {
    return null;
  }

  // Build status parts
  const parts: string[] = [];
  if (counts.generating > 0) {
    parts.push(`${counts.generating} generating now`);
  }
  if (counts.delayed > 0 || counts.failed > 0) {
    const retryCount = counts.delayed + counts.failed;
    parts.push(`${retryCount} retrying automatically`);
  }

  return (
    <div
      style={{
        marginBottom: '16px',
        padding: '12px 16px',
        backgroundColor: '#fef3c7',
        border: '1px solid #fcd34d',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#92400e',
      }}
    >
      <span style={{ marginRight: '8px' }}>⏳</span>
      {totalPending} image{totalPending !== 1 ? 's' : ''} pending description generation
      {parts.length > 0 && (
        <span style={{ color: '#78350f', marginLeft: '8px' }}>
          — {parts.join(' • ')}
        </span>
      )}
    </div>
  );
}
